<template>
    <div class="page-container">
        <div class="container-fluid">
            <!-- Navigation Breadcrumb -->
            <div class="page-navigation">
                <div class="breadcrumb-custom">
                    <router-link to="/">
                        <i class="fas fa-home"></i> Home
                    </router-link>
                    <span class="separator">/</span>
                    <router-link to="/applications">Permohonan</router-link>
                    <span class="separator">/</span>
                    <router-link :to="{name: 'application-certificate'}">Mohon Ijazah</router-link>
                    <span class="separator">/</span>
                    <span>Status</span>
                </div>
            </div>

            <!-- Page Header -->
            <div class="page-header">
                <h2 class="page-title">Status Permohonan Ijazah</h2>
                <p class="page-subtitle">Lacak progress verifikasi permohonan Anda</p>
            </div>

            <!-- Loading State -->
            <div v-if="loading" class="loading-state">
                <div class="loading-spinner-large">
                    <i class="fas fa-spinner fa-spin"></i>
                </div>
                <p>Memuat data...</p>
            </div>

            <!-- Error State -->
            <div v-else-if="errorMessage" class="row justify-content-center">
                <div class="col-12 col-lg-6">
                    <div class="error-card">
                        <div class="error-icon">
                            <i class="fas fa-exclamation-triangle"></i>
                        </div>
                        <h4>{{ errorMessage }}</h4>
                        <router-link :to="{name: 'application-certificate'}" class="btn-back">
                            <i class="fas fa-arrow-left"></i> Kembali
                        </router-link>
                    </div>
                </div>
            </div>

            <!-- Content -->
            <div v-else class="row justify-content-center">
                <div class="col-12 col-lg-10">
                    
                    <!-- Status Overview Card -->
                    <div class="status-overview-card">
                        <div class="status-header">
                            <div class="request-code">
                                <span class="code-label">Kode Permohonan</span>
                                <span class="code-value">{{ requestData.request_code }}</span>
                            </div>
                            <div class="status-indicator" :class="getOverallStatusClass(requestData.status)">
                                <i :class="getStatusIcon(requestData.status)"></i>
                                <span>{{ getStatusLabel(requestData.status) }}</span>
                            </div>
                        </div>
                        
                        <div class="status-meta">
                            <div class="meta-group">
                                <div class="meta-item">
                                    <i class="fas fa-calendar-alt"></i>
                                    <span>{{ requestData.request_date }}</span>
                                </div>
                                <div class="meta-item">
                                    <i class="fas fa-graduation-cap"></i>
                                    <span>{{ requestData.degree_level === 'diploma' ? 'Diploma' : 'Sarjana (S1)' }}</span>
                                </div>
                            </div>
                            <button @click="openPdf" class="btn-pdf">
                                <i class="fas fa-file-pdf"></i> PDF
                            </button>
                        </div>
                    </div>

                    <!-- Progress Tracker -->
                    <div class="progress-section">
                        <h5 class="section-title">
                            <i class="fas fa-tasks"></i> Progress Verifikasi
                        </h5>
                        
                        <div class="progress-tracker">
                            <div 
                                v-for="(step, index) in progressData" 
                                :key="index" 
                                class="progress-step"
                                :class="getStepClass(step.status)"
                            >
                                <div class="step-connector" v-if="index > 0"></div>
                                <div class="step-marker">
                                    <i v-if="step.status === 'completed' || step.status === 'approved'" class="fas fa-check"></i>
                                    <i v-else-if="step.status === 'rejected'" class="fas fa-times"></i>
                                    <i v-else-if="step.status === 'pending' && isCurrentStep(index)" class="fas fa-hourglass-half"></i>
                                    <span v-else>{{ step.step }}</span>
                                </div>
                                <div class="step-info">
                                    <h6 class="step-title">{{ step.name }}</h6>
                                    <p class="step-subtitle">{{ step.name_en }}</p>
                                    <span class="step-status" :class="getStatusClass(step.status)">
                                        {{ getStatusLabel(step.status) }}
                                    </span>
                                    <p v-if="step.date" class="step-date">
                                        <i class="fas fa-clock"></i> {{ step.date }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Documents Section -->
                    <div class="documents-section">
                        <h5 class="section-title">
                            <i class="fas fa-file-alt"></i> Dokumen yang Dimohon
                        </h5>
                        
                        <div class="documents-grid">
                            <div v-if="hasDocument('guarantee_diploma')" class="document-badge">
                                <i class="fas fa-file-certificate"></i>
                                <span>Surat Jaminan Kelulusan (Diploma)</span>
                            </div>
                            <div v-if="hasDocument('guarantee_bachelor')" class="document-badge">
                                <i class="fas fa-file-certificate"></i>
                                <span>Surat Jaminan Kelulusan (Sarjana)</span>
                            </div>
                            <div v-if="hasDocument('cert_arab')" class="document-badge">
                                <i class="fas fa-scroll"></i>
                                <span>Syahadah (Arab)</span>
                            </div>
                            <div v-if="hasDocument('cert_english')" class="document-badge">
                                <i class="fas fa-scroll"></i>
                                <span>Syahadah (English)</span>
                            </div>
                            <div v-if="hasDocument('cert_thai')" class="document-badge">
                                <i class="fas fa-scroll"></i>
                                <span>Syahadah (Thai)</span>
                            </div>
                            <div v-if="hasDocument('cert_malay')" class="document-badge">
                                <i class="fas fa-scroll"></i>
                                <span>Syahadah (Melayu)</span>
                            </div>
                            <div v-if="hasDocument('transcript_arab')" class="document-badge">
                                <i class="fas fa-file-invoice"></i>
                                <span>Transkrip (Arab)</span>
                            </div>
                            <div v-if="hasDocument('transcript_english')" class="document-badge">
                                <i class="fas fa-file-invoice"></i>
                                <span>Transkrip (English)</span>
                            </div>
                            <div v-if="hasDocument('transcript_thai')" class="document-badge">
                                <i class="fas fa-file-invoice"></i>
                                <span>Transkrip (Thai)</span>
                            </div>
                            <div v-if="hasDocument('transcript_malay')" class="document-badge">
                                <i class="fas fa-file-invoice"></i>
                                <span>Transkrip (Melayu)</span>
                            </div>
                        </div>
                    </div>

                    <!-- Notes Section -->
                    <div v-if="requestData.notes" class="notes-section">
                        <h5 class="section-title">
                            <i class="fas fa-sticky-note"></i> Catatan
                        </h5>
                        <p class="notes-content">{{ requestData.notes }}</p>
                    </div>

                    <!-- Actions -->
                    <div class="actions-bar">
                        <router-link to="/applications" class="btn-secondary">
                            <i class="fas fa-arrow-left"></i> Kembali
                        </router-link>
                        <button @click="refreshData" class="btn-primary" :disabled="refreshing">
                            <i :class="refreshing ? 'fas fa-spinner fa-spin' : 'fas fa-sync-alt'"></i>
                            {{ refreshing ? 'Memuat...' : 'Refresh' }}
                        </button>
                    </div>

                </div>
            </div>
        </div>
    </div>
</template>

<script>
import http from '@/services/BackendService'

export default {
    name: 'CertificateStatus',
    metaInfo: {
        title: 'Status Permohonan Ijazah'
    },
    data() {
        return {
            loading: true,
            refreshing: false,
            requestData: {},
            progressData: [],
            errorMessage: ''
        }
    },
    mounted() {
        this.loadProgress()
    },
    methods: {
        loadProgress() {
            this.loading = true
            this.$Progress.start()
            
            const requestId = this.$route.params.id
            
            http.get(`graduation/requests/${requestId}/progress`)
                .then(response => {
                    if (response.data.success) {
                        this.requestData = response.data.data.request
                        this.progressData = response.data.data.progress
                    }
                    this.loading = false
                    this.$Progress.finish()
                })
                .catch(error => {
                    this.loading = false
                    this.$Progress.fail()
                    
                    if (error.response) {
                        if (error.response.status == 401 || error.response.status == 419) {
                            this.$store.dispatch("authentication/cencelAuthen")
                        } else if (error.response.status == 404) {
                            this.errorMessage = 'Permohonan tidak ditemukan'
                        } else {
                            this.errorMessage = 'Gagal memuat data'
                        }
                    } else {
                        this.errorMessage = 'Gagal menghubungi server'
                    }
                })
        },
        refreshData() {
            this.refreshing = true
            this.$Progress.start()
            
            const requestId = this.$route.params.id
            
            http.get(`graduation/requests/${requestId}/progress`)
                .then(response => {
                    if (response.data.success) {
                        this.requestData = response.data.data.request
                        this.progressData = response.data.data.progress
                    }
                    this.refreshing = false
                    this.$Progress.finish()
                })
                .catch(() => {
                    this.refreshing = false
                    this.$Progress.fail()
                })
        },
        getStatusClass(status) {
            switch(status) {
                case 'completed':
                case 'approved':
                    return 'status-success'
                case 'rejected':
                    return 'status-danger'
                case 'pending':
                default:
                    return 'status-warning'
            }
        },
        getOverallStatusClass(status) {
            switch(status) {
                case 'completed':
                case 'approved':
                    return 'indicator-success'
                case 'rejected':
                    return 'indicator-danger'
                case 'pending':
                default:
                    return 'indicator-warning'
            }
        },
        getStatusLabel(status) {
            switch(status) {
                case 'completed':
                    return 'Selesai'
                case 'approved':
                    return 'Diluluskan'
                case 'rejected':
                    return 'Ditolak'
                case 'pending':
                default:
                    return 'Menunggu'
            }
        },
        getStatusIcon(status) {
            switch(status) {
                case 'completed':
                case 'approved':
                    return 'fas fa-check-circle'
                case 'rejected':
                    return 'fas fa-times-circle'
                case 'pending':
                default:
                    return 'fas fa-clock'
            }
        },
        getStepClass(status) {
            switch(status) {
                case 'completed':
                case 'approved':
                    return 'step-completed'
                case 'rejected':
                    return 'step-rejected'
                case 'pending':
                default:
                    return 'step-pending'
            }
        },
        isCurrentStep(index) {
            // Find the first pending step
            for (let i = 0; i < this.progressData.length; i++) {
                if (this.progressData[i].status === 'pending') {
                    return i === index
                }
            }
            return false
        },
        hasDocument(docType) {
            return this.requestData && this.requestData.documents_legacy && this.requestData.documents_legacy[docType]
        },
        openPdf() {
            const requestId = this.$route.params.id
            window.open(`${process.env.VUE_APP_URL_COOKIE}graduation/printRequestPdf/${requestId}`)
        }
    }
}
</script>

<style scoped>
/* Loading State */
.loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
}

.loading-spinner-large {
    width: 60px;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
}

.loading-spinner-large i {
    font-size: 3rem;
    color: var(--primary-color, #2d6a4f);
}

.loading-state p {
    color: var(--text-secondary, #7f8c8d);
    margin: 0;
}

/* Error Card */
.error-card {
    background: var(--bg-white, #fff);
    border-radius: var(--border-radius, 16px);
    padding: 40px;
    text-align: center;
    box-shadow: var(--shadow-md, 0 4px 15px rgba(0,0,0,0.1));
    border-top: 4px solid #e74c3c;
}

.error-icon {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
}

.error-icon i {
    font-size: 2rem;
    color: #dc2626;
}

.error-card h4 {
    color: var(--text-primary, #2c3e50);
    margin-bottom: 20px;
}

.btn-back {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: var(--primary-gradient, linear-gradient(135deg, #47c07a 0%, #2d9d6f 100%));
    color: #fff;
    padding: 12px 24px;
    border-radius: var(--border-radius-sm, 8px);
    text-decoration: none;
    font-weight: 600;
    transition: all 0.3s ease;
}

.btn-back:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(45, 106, 79, 0.3);
    color: #fff;
}

/* Status Overview Card */
.status-overview-card {
    background: var(--bg-white, #fff);
    border-radius: var(--border-radius, 16px);
    padding: 24px;
    box-shadow: var(--shadow-md, 0 4px 15px rgba(0,0,0,0.1));
    margin-bottom: 24px;
}

.status-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 16px;
    margin-bottom: 16px;
}

.request-code {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.code-label {
    font-size: 0.85rem;
    color: var(--text-secondary, #7f8c8d);
}

.code-value {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--primary-color, #2d6a4f);
    font-family: 'Monaco', 'Menlo', monospace;
    letter-spacing: 1px;
}

.status-indicator {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    border-radius: 50px;
    font-weight: 600;
    font-size: 0.95rem;
}

.status-indicator i {
    font-size: 1.1rem;
}

.indicator-success {
    background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
    color: #059669;
}

.indicator-warning {
    background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
    color: #d97706;
}

.indicator-danger {
    background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
    color: #dc2626;
}

.status-meta {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
    padding-top: 16px;
    border-top: 1px solid #f0f0f0;
}

.meta-group {
    display: flex;
    gap: 24px;
    flex-wrap: wrap;
}

.btn-pdf {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 24px;
    border-radius: var(--border-radius-sm, 8px);
    font-weight: 600;
    font-size: 0.9rem;
    cursor: pointer;
    transition: all 0.3s ease;
    background: #fff;
    color: #dc2626;
    border: 1.5px solid #dc2626;
}

.btn-pdf:hover {
    background: #dc2626;
    color: #fff;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(220, 38, 38, 0.25);
}

.meta-item {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--text-secondary, #7f8c8d);
    font-size: 0.95rem;
}

.meta-item i {
    color: var(--primary-color, #2d6a4f);
}

/* Section Styles */
.progress-section,
.documents-section,
.notes-section {
    background: var(--bg-white, #fff);
    border-radius: var(--border-radius, 16px);
    padding: 24px;
    box-shadow: var(--shadow-sm, 0 2px 8px rgba(0,0,0,0.08));
    margin-bottom: 24px;
}

.section-title {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 0 0 20px;
    color: var(--text-primary, #2c3e50);
    font-weight: 600;
    font-size: 1.1rem;
}

.section-title i {
    color: var(--primary-color, #2d6a4f);
}

/* Progress Tracker */
.progress-tracker {
    display: flex;
    flex-direction: column;
    gap: 0;
}

.progress-step {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    position: relative;
    padding-bottom: 24px;
}

.progress-step:last-child {
    padding-bottom: 0;
}

.progress-step::before {
    content: '';
    position: absolute;
    left: 20px;
    top: 44px;
    bottom: 0;
    width: 2px;
    background: #e5e7eb;
}

.progress-step:last-child::before {
    display: none;
}

.progress-step.step-completed::before {
    background: linear-gradient(180deg, #10b981 0%, #e5e7eb 100%);
}

.step-marker {
    width: 42px;
    height: 42px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 0.9rem;
    flex-shrink: 0;
    z-index: 1;
    transition: all 0.3s ease;
}

.step-completed .step-marker {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    color: #fff;
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.step-pending .step-marker {
    background: #f3f4f6;
    border: 2px dashed #d1d5db;
    color: var(--text-secondary, #7f8c8d);
}

.step-rejected .step-marker {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    color: #fff;
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.step-info {
    flex: 1;
    padding: 8px 16px;
    background: #f9fafb;
    border-radius: var(--border-radius-sm, 8px);
    border-left: 3px solid transparent;
}

.step-completed .step-info {
    background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
    border-left-color: #10b981;
}

.step-rejected .step-info {
    background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
    border-left-color: #ef4444;
}

.step-title {
    margin: 0 0 4px;
    color: var(--text-primary, #2c3e50);
    font-weight: 600;
    font-size: 1rem;
}

.step-subtitle {
    margin: 0 0 8px;
    font-size: 0.85rem;
    color: var(--text-secondary, #7f8c8d);
}

.step-status {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;
}

.status-success {
    background: rgba(16, 185, 129, 0.15);
    color: #059669;
}

.status-warning {
    background: rgba(245, 158, 11, 0.15);
    color: #d97706;
}

.status-danger {
    background: rgba(239, 68, 68, 0.15);
    color: #dc2626;
}

.step-date {
    margin: 8px 0 0;
    font-size: 0.8rem;
    color: var(--text-secondary, #7f8c8d);
}

.step-date i {
    margin-right: 4px;
}

/* Documents Grid */
.documents-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
}

.document-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
    border: 1px solid #86efac;
    border-radius: 50px;
    font-size: 0.9rem;
    color: #166534;
    font-weight: 500;
}

.document-badge i {
    color: #22c55e;
}

/* Notes */
.notes-content {
    color: var(--text-primary, #2c3e50);
    line-height: 1.7;
    margin: 0;
    padding: 16px;
    background: #f9fafb;
    border-radius: var(--border-radius-sm, 8px);
    border-left: 3px solid var(--primary-color, #2d6a4f);
}

/* Actions Bar */
.actions-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    padding: 16px 0;
}

.btn-primary,
.btn-secondary {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    border-radius: var(--border-radius-sm, 8px);
    font-weight: 600;
    font-size: 0.95rem;
    cursor: pointer;
    transition: all 0.3s ease;
    text-decoration: none;
    border: none;
}

.btn-primary {
    background: var(--primary-gradient, linear-gradient(135deg, #47c07a 0%, #2d9d6f 100%));
    color: #fff;
    box-shadow: 0 2px 8px rgba(45, 106, 79, 0.2);
}

.btn-primary:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(45, 106, 79, 0.3);
}

.btn-primary:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.btn-secondary {
    background: #f3f4f6;
    color: var(--text-secondary, #6b7280);
    border: 1px solid #e5e7eb;
}

.btn-secondary:hover {
    background: #e5e7eb;
    color: var(--text-primary, #374151);
}

/* Responsive */
@media (max-width: 768px) {
    .status-header {
        flex-direction: column;
        align-items: stretch;
    }
    
    .status-indicator {
        align-self: flex-start;
    }
    
    .code-value {
        font-size: 1.2rem;
    }
    
    .status-meta {
        flex-direction: column;
        gap: 12px;
    }
    
    .progress-step {
        gap: 12px;
    }
    
    .step-marker {
        width: 36px;
        height: 36px;
        font-size: 0.8rem;
    }
    
    .progress-step::before {
        left: 17px;
        top: 40px;
    }
    
    .documents-grid {
        flex-direction: column;
    }
    
    .document-badge {
        width: 100%;
        justify-content: flex-start;
    }
    
    .actions-bar {
        flex-direction: column-reverse;
    }
    
    .btn-primary,
    .btn-secondary {
        width: 100%;
        justify-content: center;
    }
}

@media (max-width: 480px) {
    .step-info {
        padding: 12px;
    }
    
    .step-title {
        font-size: 0.95rem;
    }
}
</style>
